import { useState, useEffect } from 'react'
import { Link, useParams } from 'react-router-dom'
import { useAuth } from '../contexts/AuthContext'
import api from '../services/api'
import { 
  FileText, 
  CheckCircle, 
  Clock, 
  AlertCircle,
  Download,
  ChevronRight,
  Upload,
  FileCheck,
  Briefcase,
  GraduationCap,
  CreditCard,
  Banknote,
  Award,
  Receipt,
  LogOut,
  Loader2
} from 'lucide-react'
import toast from 'react-hot-toast'

// Document type configuration with icons and labels
const REQUIRED_DOCUMENTS = [
  { key: 'resume', label: 'Resume', icon: FileText },
  { key: 'aadhar', label: 'Aadhar Card', icon: CreditCard },
  { key: 'pan', label: 'PAN Card', icon: CreditCard },
  { key: 'bank_cheque', label: 'Bank Chequebook', icon: Banknote },
  { key: 'passport_copy', label: 'Passport Copy', icon: FileText },
  { key: 'education', label: 'Educational Document', icon: GraduationCap },
  { key: 'experience_certificate', label: 'Experience Certificates', icon: Award },
  { key: 'salary_slip', label: '3 Month Salary Slips', icon: Receipt },
  { key: 'resignation_letter', label: 'Resignation Letter', icon: LogOut },
  { key: 'form16', label: 'Form 16', icon: FileText },
  { key: 'form26as', label: 'Form 26AS', icon: FileText },
  { key: 'investment_declaration', label: 'Investment Declaration (Excel)', icon: FileText },
]

// Map document types to required document categories
const mapDocumentType = (docType) => {
  const type = docType.toLowerCase()
  if (type.includes('resume')) return 'resume'
  if (type.includes('aadhar')) return 'aadhar'
  if (type.includes('pan')) return 'pan'
  if (type.includes('passport')) return 'passport_copy'
  if (type.includes('bank') || type.includes('cheque') || type.includes('check')) return 'bank_cheque'
  if (type.includes('education') || type.includes('degree') || type.includes('diploma') || type.includes('marksheet')) return 'education'
  if (type.includes('experience') || type.includes('certificate')) return 'experience_certificate'
  if (type.includes('salary') || type.includes('slip') || type.includes('payslip')) return 'salary_slip'
  if (type.includes('resignation') || type.includes('relieving')) return 'resignation_letter'
  if (type.includes('form16') || type.includes('form_16')) return 'form16'
  if (type.includes('form26as') || type.includes('form_26as') || type.includes('26as')) return 'form26as'
  if (type.includes('investment') || type.includes('declaration')) return 'investment_declaration'
  return null
}

function DocumentStatusSection({ documents }) {
  // Get uploaded document types
  const uploadedTypes = new Set()
  documents.forEach(doc => {
    const mappedType = mapDocumentType(doc.document_type)
    if (mappedType) uploadedTypes.add(mappedType)
  })

  // Check education documents for tags (bundle vs individual)
  const educationDocs = documents.filter(doc => mapDocumentType(doc.document_type) === 'education')
  const educationHasBundle = educationDocs.some(doc => 
    doc.original_filename.toLowerCase().includes('bundle') ||
    doc.original_filename.toLowerCase().includes('combined') ||
    doc.original_filename.toLowerCase().includes('all')
  )
  const educationTags = educationHasBundle ? ['Bundle'] : educationDocs.length > 0 ? ['Individual'] : []

  return (
    <div className="card">
      <h3 className="section-header flex items-center">
        <FileCheck className="h-5 w-5 mr-2 text-primary-600" />
        Document Status
      </h3>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {REQUIRED_DOCUMENTS.map((doc) => {
          const isUploaded = uploadedTypes.has(doc.key)
          const Icon = doc.icon
          
          return (
            <div
              key={doc.key}
              className={`flex items-center p-4 rounded-lg border ${
                isUploaded
                  ? 'bg-green-50 border-green-200'
                  : 'bg-gray-50 border-gray-200'
              }`}
            >
              <div className={`flex-shrink-0 w-10 h-10 rounded-full flex items-center justify-center mr-3 ${
                isUploaded
                  ? 'bg-green-500 text-white'
                  : 'bg-gray-300 text-gray-600'
              }`}>
                {isUploaded ? (
                  <CheckCircle className="h-5 w-5" />
                ) : (
                  <Icon className="h-5 w-5" />
                )}
              </div>
              <div className="flex-1 min-w-0">
                <span className={`block font-medium text-sm ${
                  isUploaded ? 'text-green-800' : 'text-gray-700'
                }`}>
                  {doc.label}
                </span>
                {doc.key === 'education' && isUploaded && educationTags.length > 0 && (
                  <span className="inline-block mt-1 px-2 py-0.5 text-xs bg-blue-100 text-blue-700 rounded-full">
                    {educationTags[0]}
                  </span>
                )}
                {!isUploaded && (
                  <span className="inline-flex items-center mt-1 text-xs text-gray-500">
                    <Upload className="h-3 w-3 mr-1" />
                    Pending
                  </span>
                )}
              </div>
            </div>
          )
        })}
      </div>
      <div className="mt-4 p-3 bg-amber-50 border border-amber-200 rounded-lg">
        <p className="text-sm text-amber-800">
          <span className="font-medium">Note:</span> Educational documents can be uploaded as a single bundle or individual certificates. 
          Upload all required documents to complete your onboarding.
        </p>
      </div>
    </div>
  )
}

function Dashboard() {
  const [employee, setEmployee] = useState(null)
  const [loading, setLoading] = useState(true)
  const [documents, setDocuments] = useState([])
  const [documentComments, setDocumentComments] = useState({})
  const [sectionComments, setSectionComments] = useState([])
  const [uploadState, setUploadState] = useState({ target: null, state: 'idle', message: '' })
  const [isDownloadingDocsZip, setIsDownloadingDocsZip] = useState(false)
  const [uploadRows, setUploadRows] = useState({
    education: [{ id: 1, label: '' }],
    experience_certificate: [{ id: 1, label: '' }],
    salary_slip: [{ id: 1, label: '' }],
    passport_copy: [{ id: 1, label: '' }]
  })
  const { companySlug } = useParams()
  const { user } = useAuth()

  useEffect(() => {
    fetchDashboardData()
  }, [companySlug])

  const fetchDashboardData = async () => {
    try {
      setLoading(true)
      const [empRes, docRes, sectionCommentsRes] = await Promise.all([
        api.get(`/${companySlug}/employee/me`),
        api.get(`/${companySlug}/documents`),
        api.get(`/${companySlug}/employee/me/comments`)
      ])
      setEmployee(empRes.data)
      setDocuments(docRes.data)
      setSectionComments(sectionCommentsRes.data || [])
      if (docRes.data?.length) {
        const commentPairs = await Promise.all(
          docRes.data.map(async (doc) => {
            try {
              const res = await api.get(`/${companySlug}/employee/me/documents/${doc.id}/comments`)
              return [doc.id, res.data]
            } catch (e) {
              return [doc.id, []]
            }
          })
        )
        const next = {}
        commentPairs.forEach(([id, list]) => { next[id] = list })
        setDocumentComments(next)
      } else {
        setDocumentComments({})
      }
    } catch (error) {
      toast.error('Failed to load dashboard data')
    } finally {
      setLoading(false)
    }
  }

  const pollExtractionResult = async (documentId, targetKey) => {
    const poll = async () => {
      try {
        const response = await api.get(`/${companySlug}/documents/${documentId}/extraction`)
        const result = response.data

        if (result.status === 'completed' || result.status === 'failed') {
          const docRes = await api.get(`/${companySlug}/documents`)
          setDocuments(docRes.data)
          setUploadState({ target: null, state: 'idle', message: '' })
        } else {
          setUploadState({ target: targetKey, state: 'processing', message: 'Extracting…' })
          setTimeout(poll, 3000)
        }
      } catch (error) {
        setUploadState({ target: targetKey, state: 'error', message: 'Extraction failed.' })
      }
    }
    poll()
  }

  const handleFileUpload = async (e, documentType, targetKey) => {
    const file = e.target.files?.[0]
    if (!file) return

    const formData = new FormData()
    formData.append('file', file)

    try {
      setUploadState({ target: targetKey, state: 'uploading', message: 'Uploading…' })
      toast.loading('Uploading document...', { id: 'upload' })
      const response = await api.post(
        `/${companySlug}/documents/upload?document_type=${documentType}`,
        formData
      )
      toast.success('Document uploaded', { id: 'upload' })

      const docRes = await api.get(`/${companySlug}/documents`)
      setDocuments(docRes.data)

      if (documentType === 'photo') {
        setUploadState({ target: null, state: 'idle', message: '' })
      } else {
        setUploadState({ target: targetKey, state: 'processing', message: 'Extracting…' })
        pollExtractionResult(response.data.document_id, targetKey)
      }
    } catch (error) {
      setUploadState({ target: targetKey, state: 'error', message: 'Upload failed.' })
      toast.error('Failed to upload document', { id: 'upload' })
    } finally {
      e.target.value = ''
    }
  }

  const handleRectifyDocComment = async (commentId, docId) => {
    try {
      const response = await api.patch(`/${companySlug}/employee/me/documents/${commentId}/rectify`, {})
      setDocumentComments((prev) => ({
        ...prev,
        [docId]: (prev[docId] || []).map((c) => (c.id === commentId ? response.data : c))
      }))
      toast.success('Marked as rectified')
    } catch (error) {
      toast.error('Failed to update comment')
    }
  }

  const handleExportPDF = async () => {
    try {
      const response = await api.get(`/${companySlug}/exports/personal-info/${employee.id}`, {
        responseType: 'blob'
      })
      const url = window.URL.createObjectURL(new Blob([response.data]))
      const link = document.createElement('a')
      link.href = url
      link.setAttribute('download', `Personal_Info_${employee.full_name || employee.email}.pdf`)
      document.body.appendChild(link)
      link.click()
      link.remove()
      toast.success('PDF downloaded successfully')
    } catch (error) {
      toast.error('Failed to export PDF')
    }
  }

  const handleDownloadDocumentsZip = async () => {
    if (isDownloadingDocsZip) return
    try {
      setIsDownloadingDocsZip(true)
      const response = await api.get(`/${companySlug}/documents/download/all`, {
        responseType: 'blob'
      })
      const blob = new Blob([response.data], { type: 'application/zip' })
      const url = window.URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      const fallbackName = `employee_${employee?.id || 'me'}_documents.zip`
      const disposition = response.headers['content-disposition'] || ''
      const filenameMatch = disposition.match(/filename="?([^\"]+)"?/) 
      link.setAttribute('download', filenameMatch?.[1] || fallbackName)
      document.body.appendChild(link)
      link.click()
      link.remove()
      toast.success('Documents ZIP downloaded')
    } catch (error) {
      toast.error('Failed to download documents ZIP')
    } finally {
      setIsDownloadingDocsZip(false)
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    )
  }

  const sections = [
    { key: 'A', name: 'Employee Header', completed: employee?.section_completion_status?.A },
    { key: 'B', name: 'Personal Information', completed: employee?.section_completion_status?.B },
    { key: 'C', name: 'Contact & Address', completed: employee?.section_completion_status?.C },
    { key: 'D', name: 'Identity Documents', completed: employee?.section_completion_status?.D },
    { key: 'E', name: 'Bank Details', completed: employee?.section_completion_status?.E },
    { key: 'F', name: 'Family Information', completed: employee?.section_completion_status?.F },
    { key: 'G', name: 'Emergency Contact', completed: employee?.section_completion_status?.G },
    { key: 'H', name: 'Education', completed: employee?.section_completion_status?.H },
    { key: 'I', name: 'Experience', completed: employee?.section_completion_status?.I },
    { key: 'J', name: 'Previous Employment', completed: employee?.section_completion_status?.J },
    { key: 'K', name: 'HR Fields', completed: employee?.section_completion_status?.K },
  ]
  const completedCount = sections.filter(s => s.completed).length
  const computedPercentage = Math.round((completedCount / sections.length) * 100)
  const completionPercentage = typeof employee?.completion_percentage === 'number'
    ? (employee.completion_percentage > 0 || completedCount === 0
        ? employee.completion_percentage
        : computedPercentage)
    : computedPercentage
  const isSubmitted = employee?.is_submitted
  const isLocked = employee?.is_locked
  const uploadedByType = {}
  documents.forEach((doc) => {
    const mappedType = mapDocumentType(doc.document_type)
    if (mappedType && !uploadedByType[mappedType]) {
      uploadedByType[mappedType] = doc
    }
  })
  const multiUploadTypes = new Set(['education', 'experience_certificate', 'salary_slip', 'passport_copy'])
  const buildDocumentType = (baseType, label) => {
    if (!label) return baseType
    return `${baseType}:${label}`
  }
  const addUploadRow = (type) => {
    setUploadRows((prev) => {
      const nextId = (prev[type]?.[prev[type].length - 1]?.id || 0) + 1
      const next = { id: nextId, label: '' }
      return { ...prev, [type]: [...(prev[type] || []), next] }
    })
  }
  const updateUploadLabel = (type, rowId, value) => {
    setUploadRows((prev) => ({
      ...prev,
      [type]: prev[type].map((row) => (row.id === rowId ? { ...row, label: value } : row))
    }))
  }

  return (
    <div className="space-y-6">
      {/* Welcome Header */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">
              Welcome, {employee?.full_name || employee?.email}
            </h1>
            <p className="mt-1 text-gray-600">
              Complete your onboarding to get started
            </p>
          </div>
          {isSubmitted && (
            <div className="flex items-center text-green-600">
              <CheckCircle className="h-6 w-6 mr-2" />
              <span className="font-medium">Submitted</span>
            </div>
          )}
        </div>
      </div>

      {/* Progress Overview */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {/* Progress Card */}
        <div className="card">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-900">Progress</h3>
            <Clock className="h-5 w-5 text-gray-400" />
          </div>
          <div className="flex items-center">
            <div className="relative w-24 h-24 mr-4">
              <svg className="w-24 h-24 transform -rotate-90">
                <circle
                  cx="48"
                  cy="48"
                  r="40"
                  stroke="#e5e7eb"
                  strokeWidth="8"
                  fill="none"
                />
                <circle
                  cx="48"
                  cy="48"
                  r="40"
                  stroke={completionPercentage === 100 ? '#10B981' : '#3B82F6'}
                  strokeWidth="8"
                  fill="none"
                  strokeDasharray={`${completionPercentage * 2.51} 251`}
                  strokeLinecap="round"
                />
              </svg>
              <span className="absolute inset-0 flex items-center justify-center text-xl font-bold text-gray-900">
                {completionPercentage}%
              </span>
            </div>
            <div>
              <p className="text-sm text-gray-600">
                {completionPercentage === 100 
                  ? 'All sections completed!' 
                  : `${sections.filter(s => s.completed).length} of ${sections.length} sections done`}
              </p>
              {!isSubmitted && completionPercentage === 100 && (
                <p className="text-sm text-amber-600 mt-1">
                  Ready to submit!
                </p>
              )}
            </div>
          </div>
        </div>

        {/* Quick Actions */}
        <div className="card">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-900">Quick Actions</h3>
            <FileText className="h-5 w-5 text-gray-400" />
          </div>
          <div className="space-y-3">
            <Link
              to={`/${companySlug}/onboarding`}
              className="flex items-center justify-between p-3 bg-primary-50 rounded-lg hover:bg-primary-100 transition-colors"
            >
              <span className="font-medium text-primary-700">
                {isSubmitted ? 'View Form' : 'Continue Form'}
              </span>
              <ChevronRight className="h-5 w-5 text-primary-600" />
            </Link>
            
            {isSubmitted && (
              <>
                <button
                  onClick={handleExportPDF}
                  className="w-full flex items-center justify-between p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
                >
                  <span className="font-medium text-gray-700">Export Personal Info (PDF)</span>
                  <Download className="h-5 w-5 text-gray-600" />
                </button>
                <button
                  onClick={handleDownloadDocumentsZip}
                  className="w-full flex items-center justify-between p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
                  disabled={isDownloadingDocsZip}
                >
                  <span className="font-medium text-gray-700">
                    {isDownloadingDocsZip ? 'Preparing Documents ZIP...' : 'Download Documents ZIP'}
                  </span>
                  {isDownloadingDocsZip ? (
                    <Loader2 className="h-5 w-5 text-gray-600 animate-spin" />
                  ) : (
                    <Download className="h-5 w-5 text-gray-600" />
                  )}
                </button>
              </>
            )}
          </div>
        </div>

        {/* Documents Summary */}
        <div className="card">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-900">Documents</h3>
            <AlertCircle className="h-5 w-5 text-gray-400" />
          </div>
          <div className="space-y-2">
            <div className="flex justify-between text-sm">
              <span className="text-gray-600">Total uploaded</span>
              <span className="font-medium">{documents.length}</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-gray-600">Processing</span>
              <span className="font-medium text-amber-600">
                {documents.filter(d => d.extraction_status === 'processing').length}
              </span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-gray-600">Extracted</span>
              <span className="font-medium text-green-600">
                {documents.filter(d => d.extraction_status === 'completed').length}
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Section Status */}
      <div className="card">
        <h3 className="section-header">Section Status</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {sections.map((section) => (
            <div
              key={section.key}
              className={`flex items-center p-4 rounded-lg border ${
                section.completed
                  ? 'bg-green-50 border-green-200'
                  : 'bg-gray-50 border-gray-200'
              }`}
            >
              <div className={`flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center mr-3 ${
                section.completed
                  ? 'bg-green-500 text-white'
                  : 'bg-gray-300 text-gray-600'
              }`}>
                {section.completed ? (
                  <CheckCircle className="h-5 w-5" />
                ) : (
                  <span className="text-sm font-medium">{section.key}</span>
                )}
              </div>
              <span className={`font-medium ${
                section.completed ? 'text-green-800' : 'text-gray-700'
              }`}>
                {section.name}
              </span>
            </div>
          ))}
        </div>
      </div>

      {/* Document Status */}
      <DocumentStatusSection documents={documents} />

      {/* Section Review Status */}
      <div className="card">
        <h3 className="section-header flex items-center">
          <AlertCircle className="h-5 w-5 mr-2 text-amber-600" />
          Section Review Status
        </h3>
        {sectionComments.filter((c) => c.status !== 'rectified').length === 0 ? (
          <p className="text-sm text-gray-500">No pending section comments.</p>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {sectionComments
              .filter((c) => c.status !== 'rectified')
              .map((c) => (
                <div key={c.id} className="p-3 border border-amber-200 bg-amber-50 rounded-lg">
                  <div className="text-sm font-medium text-amber-900">
                    Section {c.section_key}
                  </div>
                  <div className="text-sm text-amber-800">{c.comment}</div>
                </div>
              ))}
          </div>
        )}
      </div>

      {/* Document Comments */}
      <div className="card">
        <h3 className="section-header flex items-center">
          <FileText className="h-5 w-5 mr-2 text-primary-600" />
          Document Review Comments
        </h3>
        {documents.length === 0 ? (
          <p className="text-sm text-gray-500">No documents uploaded yet.</p>
        ) : (
          <div className="space-y-4">
            {documents.map((doc) => {
              const comments = documentComments[doc.id] || []
              if (comments.length === 0) return null
              return (
                <div key={doc.id} className="border rounded-lg p-4 bg-gray-50">
                  <div className="text-sm font-medium text-gray-900 mb-2">{doc.original_filename}</div>
                  <div className="space-y-2">
                    {comments.map((comment) => (
                      <div key={comment.id} className="p-3 bg-white rounded border">
                        <div className="flex items-start justify-between gap-3">
                          <div className="text-sm text-gray-800">{comment.comment}</div>
                          <div className="flex items-center gap-2">
                            <span className={`text-xs px-2 py-1 rounded ${comment.status === 'rectified' ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-800'}`}>
                              {comment.status === 'rectified' ? 'Rectified' : 'Open'}
                            </span>
                            {comment.status !== 'rectified' && (
                              <button
                                type="button"
                                onClick={() => handleRectifyDocComment(comment.id, doc.id)}
                                className="text-xs font-medium text-primary-700 hover:text-primary-900"
                              >
                                Mark Rectified
                              </button>
                            )}
                          </div>
                        </div>
                        {comment.rectified_note && (
                          <div className="text-xs text-gray-500 mt-1">Your note: {comment.rectified_note}</div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )
            })}
          </div>
        )}
      </div>

      {/* Document Uploads */}
      <div className="card">
        <h3 className="section-header flex items-center">
          <Upload className="h-5 w-5 mr-2 text-primary-600" />
          Upload Documents
        </h3>
        {isLocked && (
          <p className="text-sm text-amber-700 mb-4">
            Your form is locked. Document uploads are disabled.
          </p>
        )}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {REQUIRED_DOCUMENTS.map((doc) => {
            const uploadedDoc = uploadedByType[doc.key]
            const rows = multiUploadTypes.has(doc.key) ? uploadRows[doc.key] : [{ id: 1, label: '' }]

            return (
              <div key={doc.key} className="p-4 rounded-lg border border-gray-200 bg-white">
                <div className="font-medium text-gray-900">{doc.label}</div>
                {uploadedDoc ? (
                  <div className="text-xs text-green-700 mt-1 truncate">
                    Latest: {uploadedDoc.original_filename}
                  </div>
                ) : (
                  <div className="text-xs text-gray-500 mt-1">No file uploaded yet</div>
                )}

                <div className="mt-3 space-y-3">
                  {rows.map((row) => {
                    const targetKey = `${doc.key}:${row.id}`
                    const isUploading = uploadState.target === targetKey && uploadState.state === 'uploading'
                    const isProcessing = uploadState.target === targetKey && uploadState.state === 'processing'
                    const isError = uploadState.target === targetKey && uploadState.state === 'error'
                    const docTypeForUpload = buildDocumentType(doc.key, row.label.trim())

                    return (
                      <div key={row.id} className="flex flex-col md:flex-row md:items-center gap-3">
                        {multiUploadTypes.has(doc.key) && (
                          <input
                            type="text"
                            placeholder={doc.key === 'salary_slip'
                              ? 'e.g., Jan 2025'
                              : doc.key === 'experience_certificate'
                              ? 'e.g., Company 1'
                              : doc.key === 'passport_copy'
                              ? 'e.g., Passport 1'
                              : 'e.g., Degree / Institute'}
                            value={row.label}
                            onChange={(e) => updateUploadLabel(doc.key, row.id, e.target.value)}
                            className="input md:flex-1"
                          />
                        )}
                        <div className="flex items-center gap-3">
                          <label className={`inline-flex items-center px-3 py-2 rounded-md text-sm font-medium border ${
                            isUploading || isProcessing || isLocked
                              ? 'bg-gray-100 text-gray-400 border-gray-200 cursor-not-allowed'
                              : 'bg-primary-50 text-primary-700 border-primary-200 hover:bg-primary-100 cursor-pointer'
                          }`}>
                            <Upload className="h-4 w-4 mr-2" />
                            {uploadedDoc ? 'Upload' : 'Upload'}
                            <input
                              type="file"
                              accept=".pdf,.docx,.xlsx,.xls,.jpg,.jpeg,.png,.heic,.webp"
                              onChange={(e) => handleFileUpload(e, docTypeForUpload, targetKey)}
                              disabled={isUploading || isProcessing || isLocked}
                              className="hidden"
                            />
                          </label>
                          {isProcessing && (
                            <span className="text-xs text-amber-700">Extracting…</span>
                          )}
                          {isError && (
                            <span className="text-xs text-red-600">Upload failed.</span>
                          )}
                        </div>
                      </div>
                    )
                  })}
                </div>

                {multiUploadTypes.has(doc.key) && (
                  <div className="mt-3">
                    <button
                      type="button"
                      onClick={() => addUploadRow(doc.key)}
                      className="text-sm text-primary-700 hover:text-primary-800"
                      disabled={isLocked}
                    >
                      + Add more
                    </button>
                  </div>
                )}
              </div>
            )
          })}
        </div>
        <p className="text-xs text-gray-500 mt-3">
          Supported: PDF, DOCX, XLSX, JPG, PNG, HEIC, WEBP (max 10MB). Upload multiple documents by repeating the upload per item.
        </p>
      </div>

      {/* Last Saved */}
      {employee?.last_saved_at && (
        <p className="text-sm text-gray-500 text-center">
          Last saved: {new Date(employee.last_saved_at).toLocaleString()}
        </p>
      )}
    </div>
  )
}

export default Dashboard
